import axios from 'axios';

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5001'),
  timeout: 30000, // Increased timeout for production
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle network errors
    if (!error.response) {
      console.error('Network error:', error.message);
      return Promise.reject({
        message: 'Network error. Please check your connection and try again.',
        isNetworkError: true
      });
    }

    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      // Clear auth data and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      delete apiClient.defaults.headers.common['Authorization'];
      
      // Only redirect if not already on login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }

    // Handle 429 Too Many Requests
    if (error.response?.status === 429) {
      return Promise.reject({
        message: 'Too many requests. Please wait a moment and try again.',
        status: 429
      });
    }

    // Handle 500 Server Errors
    if (error.response?.status >= 500) {
      return Promise.reject({
        message: 'Server error. Please try again later.',
        status: error.response.status
      });
    }

    // Return error with user-friendly message
    const errorMessage = error.response?.data?.error || 
                         error.response?.data?.message || 
                         error.message || 
                         'An error occurred';
    
    return Promise.reject({
      message: errorMessage,
      status: error.response?.status,
      data: error.response?.data
    });
  }
);

// API endpoints
export const adminAPI = {
  getStats: () => apiClient.get('/api/admin/stats'),
  getUsers: (params) => apiClient.get('/api/admin/users', { params }),
  getItems: (params) => apiClient.get('/api/admin/items', { params }),
  createItem: (data) => apiClient.post('/api/admin/items', data),
  createBox: (data) => apiClient.post('/api/admin/boxes', data),
  updateUser: (id, data) => apiClient.put(`/api/admin/users/${id}`, data),
};

export const authAPI = {
  login: (data) => apiClient.post('/api/auth/login', data),
  register: (data) => apiClient.post('/api/auth/register', data),
  getProfile: () => apiClient.get('/api/auth/profile'),
  getStatus: () => apiClient.get('/api/auth/status'),
};

export const boxAPI = {
  getAll: () => apiClient.get('/api/boxes'),
  getById: (id) => apiClient.get(`/api/boxes/${id}`),
  purchase: (id) => apiClient.post(`/api/boxes/${id}/purchase`),
  open: (id) => apiClient.post(`/api/boxes/${id}/open`),
};

export const walletAPI = {
  getWallet: () => apiClient.get('/api/wallet'),
  getTransactions: () => apiClient.get('/api/wallet/transactions'),
  addFunds: (data) => apiClient.post('/api/wallet/add-funds', data),
};

export const inventoryAPI = {
  getInventory: (params) => apiClient.get('/api/inventory', { params }),
  getStats: () => apiClient.get('/api/inventory/stats'),
  sellItem: (id) => apiClient.post(`/api/inventory/items/${id}/sell`),
};

export default apiClient;
